import { useMemo } from "react";
import { useDialog } from "@components/common/dialog/dialog-provider";
import {
  SHARE_PROJECT_DIALOG_PROVIDER_PROPS,
  ShareProject,
} from "@components/project/share-project";
import { useAppParams } from "@router/router-helper";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { isProjectDownloaded, trackShareProject } from "@utils/project-utils";
import { useProjectArchiveDialog } from "@hooks/projects/use-project-archive-dialog";
import { BaseProjectProps } from "@custom-types/project-types";
import { useAppSelector } from "@store/store-helper";
import {
  isProjectActiveSelector,
  isProjectArchivingAllowedSelector,
  isProjectUnarchivingAllowedSelector,
} from "@store/projects/projects-selector";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";

interface UseProjectActionsReturn {
  /** Function to handle sharing the project */
  handleShareProject: () => void;

  /** Function to archive or unarchive the project. Returns true if successful, false otherwise */
  archiveOrUnarchiveProject: () => Promise<boolean>;

  /** Flag indicating if the project is currently active */
  isProjectActive: boolean;

  /** Flag indicating if the user is allowed to archive or unarchive the project */
  isAllowedToArchiveOrUnarchive: boolean;

  /** Flag indicating if the archive action is disabled due to insufficient permissions or project state */
  isArchiveActionDisabled: boolean;

  /** Flag indicating if the user can delete the project */
  canDeleteProject: boolean;

  /** Label for the archive action: "Archive" for active projects, "Unarchive" for archived projects */
  archiveActionLabel: string;
}

/**
 * Hook that provides reusable project actions like share, archive, etc.
 * Can be used in both buttons and menu items
 */
export function useProjectActions (project: BaseProjectProps["project"]): UseProjectActionsReturn {
  const { createDialog } = useDialog();
  const { trackEvent } = useTrackEvent();
  const { projectTabs } = useAppParams();
  const archiveOrUnarchiveProject = useProjectArchiveDialog({ project });

  // Selectors for project state
  const isProjectActive = useAppSelector(isProjectActiveSelector(project.id));
  const isArchivingAllowed = useAppSelector(
    isProjectArchivingAllowedSelector(project)
  );
  const isUnarchivingAllowed = useAppSelector(
    isProjectUnarchivingAllowedSelector(project)
  );

  // Permission and feature checks
  const { canDeleteProject } = useHasUserValidPermissionProjectLevel({ project });
  const { canArchiveProject, canUnarchiveProject } =
    useHasValidFeatureProjectLevel();

  /**
   * An active project can be archived if the user has the required role.
   * An archived project can be unarchived if the user has the required role and the project has not been downloaded.
   */
  const isAllowedToArchiveOrUnarchive = useMemo(() => {
    return isProjectActive
      ? canArchiveProject
      : canUnarchiveProject && !isProjectDownloaded(project);
  }, [canArchiveProject, canUnarchiveProject, isProjectActive, project]);

  /**
   * Handles share project action
   */
  function handleShareProject(): void {
    trackShareProject({
      ...project,
      eventLocation: `project ${projectTabs} page`,
      trackEvent,
    });

    void createDialog(
      SHARE_PROJECT_DIALOG_PROVIDER_PROPS,
      <ShareProject projectId={project.id} />
    );
  };

  /**
   * Determines if archive/unarchive action is disabled
   */
  const isArchiveActionDisabled = isProjectActive
    ? !isArchivingAllowed
    : !isUnarchivingAllowed;

  /**
   * Get the appropriate label for archive action based on project state
   */
  const archiveActionLabel = isProjectActive ? "Archive" : "Unarchive";

  return {
    handleShareProject,
    archiveOrUnarchiveProject,
    isProjectActive,
    isAllowedToArchiveOrUnarchive,
    isArchiveActionDisabled,
    canDeleteProject,
    archiveActionLabel,
  };
};
